import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import { Caption, COLORS, FONTS, SceneTransition, SlideTimeBar } from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, FEATURE, CORRECTION, BUG1, BUG2, FIX, LEARNINGS, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 24,
    }}
  >
    {"▍"}
    {text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 88 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 1.0,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** One knowledge-base source row with a chunk-count badge. */
const SourceRow: React.FC<{ name: string; chunks: number; ok: boolean }> = ({ name, chunks, ok }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: 1040,
      padding: "13px 20px",
      border: `1px solid ${ok ? COLORS.green : COLORS.red}`,
      borderRadius: 10,
      background: COLORS.panel,
      marginBottom: 11,
      fontFamily: FONTS.mono,
      fontSize: 22,
    }}
  >
    <span style={{ color: COLORS.dim }}>{name}</span>
    <span style={{ color: ok ? COLORS.green : COLORS.red, fontWeight: 700 }}>
      {chunks} chunks
    </span>
  </div>
);

/** A struck-through claim (a red herring or a rejected idea). */
const StrikeLine: React.FC<{ label: string; note: string }> = ({ label, note }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 16,
      width: 1200,
      marginBottom: 14,
      fontFamily: FONTS.mono,
      fontSize: 26,
    }}
  >
    <span style={{ color: COLORS.dim, textDecoration: "line-through" }}>{label}</span>
    <span style={{ color: COLORS.dim, fontSize: 20 }}>{note}</span>
  </div>
);

/* 01 - HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const fade = interpolate(frame, [30, 60], [1, 0.25], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="mission memory" color={COLORS.cyan} />
          <Big>
            your AI did the work
            <br />
            then <span style={{ color: COLORS.red, opacity: fade }}>forgot</span> it ever happened
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 - PROBLEM */
export const Scene02Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const nope = interpolate(frame, [330, 380], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the gap" color={COLORS.amber} />
          <div style={{ display: "flex", gap: 26, alignItems: "center", marginBottom: 40 }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.text }}>mission output</div>
            <div style={{ color: COLORS.dim, fontSize: 30 }}>{"→"}</div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.dim }}>blackboard + email</div>
            <div style={{ color: COLORS.dim, fontSize: 30 }}>{"→"}</div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.red }}>a locked drawer</div>
          </div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 28, color: COLORS.dim }}>
            chat: "what ideas did we come up with?"{" "}
            <span style={{ color: COLORS.red, opacity: nope, fontWeight: 700 }}>nothing found</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={PROBLEM} accent="amber" />
    </AbsoluteFill>
  );
};

/* 03 - FEATURE */
export const Scene03Feature: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.feature.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the fix" color={COLORS.green} />
        <Big size={80}>
          it <span style={{ color: COLORS.green }}>files itself</span>
          <br />
          into the searchable library
        </Big>
        <div style={{ display: "flex", gap: 22, alignItems: "center", marginTop: 46, fontFamily: FONTS.mono, fontSize: 26 }}>
          <span style={{ color: COLORS.text }}>mission finishes</span>
          <span style={{ color: COLORS.green }}>{"→"}</span>
          <span style={{ color: COLORS.green }}>auto capture</span>
          <span style={{ color: COLORS.green }}>{"→"}</span>
          <span style={{ color: COLORS.text }}>chunk + embed</span>
          <span style={{ color: COLORS.green }}>{"→"}</span>
          <span style={{ color: COLORS.cyan }}>searchable</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={FEATURE} accent="green" />
  </AbsoluteFill>
);

/* 04 - CORRECTION */
export const Scene04Correction: React.FC = () => {
  const frame = useCurrentFrame();
  const swap = interpolate(frame, [200, 240], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.correction.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the correction" color={COLORS.violet} />
          <div style={{ display: "flex", gap: 30, alignItems: "center" }}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 30,
                color: COLORS.dim,
                textDecoration: "line-through",
                border: `1px solid ${COLORS.line}`,
                borderRadius: 10,
                padding: "14px 22px",
              }}
            >
              Save to Knowledge Base
            </div>
            <div style={{ color: COLORS.dim, fontSize: 34, opacity: swap }}>{"→"}</div>
            <div
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 44,
                color: COLORS.green,
                opacity: swap,
              }}
            >
              automatic
            </div>
          </div>
          <div style={{ marginTop: 40, fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, maxWidth: 1200, textAlign: "center" }}>
            "why is filing my own work my job?"
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={CORRECTION} accent="violet" />
    </AbsoluteFill>
  );
};

/* 05 - BUG1 (empty shells) */
export const Scene05Bug1: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.bug1.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the test failed" color={COLORS.red} />
        <SourceRow name="Mission: Content Ideas . drafts/content_ideas" chunks={0} ok={false} />
        <SourceRow name="Mission: Content Ideas . drafts/content_ideas_list" chunks={0} ok={false} />
        <SourceRow name="Mission: Content Ideas . drafts/final_email_body" chunks={0} ok={false} />
        <SourceRow name="Mission: Content Ideas . answers/comparison" chunks={0} ok={false} />
        <div style={{ marginTop: 18, fontFamily: FONTS.mono, fontSize: 24, color: COLORS.red }}>
          right names, zero content. hollow shells.
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={BUG1} accent="red" />
  </AbsoluteFill>
);

/* 06 - BUG2 (red herrings + real cause) */
export const Scene06Bug2: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = interpolate(frame, [380, 430], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.bug2.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the hunt" color={COLORS.magenta} />
          <StrikeLine label="created before the code existed" note="a timezone bug" />
          <StrikeLine label="the content field was empty" note="just the worker freeing memory" />
          <StrikeLine label="the chunker is broken" note="it only zeroes on empty input" />
          <div
            style={{
              marginTop: 24,
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 46,
              color: COLORS.amber,
              opacity: reveal,
              textAlign: "center",
              maxWidth: 1400,
            }}
          >
            the embeddings service was down for one minute
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={BUG2} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 - FIX (reproduced live) */
export const Scene07Fix: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="proof, on live data" color={COLORS.green} />
        <SourceRow name="Mission: Content Ideas . drafts/content_ideas" chunks={2} ok />
        <SourceRow name="Mission: Content Ideas . drafts/content_ideas_list" chunks={2} ok />
        <SourceRow name="Mission: Content Ideas . drafts/final_email_body" chunks={2} ok />
        <SourceRow name="Mission: Content Ideas . answers/comparison" chunks={1} ok />
        <div style={{ marginTop: 18, fontFamily: FONTS.mono, fontSize: 24, color: COLORS.green }}>
          indexed: 4 . every embedding call healthy
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={FIX} accent="green" />
  </AbsoluteFill>
);

/* 08 - LEARNINGS */
export const Scene08Learnings: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.learnings.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="what stuck" color={COLORS.cyan} />
        <div style={{ display: "flex", flexDirection: "column", gap: 30, maxWidth: 1440 }}>
          <div style={{ display: "flex", gap: 22, alignItems: "baseline" }}>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.cyan }}>1</span>
            <span style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 42, color: COLORS.text, lineHeight: 1.1 }}>
              compiles and passes tests is not the same as works. prove it on real data.
            </span>
          </div>
          <div style={{ display: "flex", gap: 22, alignItems: "baseline" }}>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.green }}>2</span>
            <span style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 42, color: COLORS.text, lineHeight: 1.1 }}>
              when you write data, expect the thing downstream to be down for a minute.
            </span>
          </div>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={LEARNINGS} accent="cyan" />
  </AbsoluteFill>
);

/* 09 - OUTRO */
export const Scene09Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const glow = 0.6 + 0.4 * Math.sin(frame / 16);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <div
            style={{
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 104,
              letterSpacing: "-0.02em",
              color: COLORS.text,
              textShadow: `0 0 40px rgba(34,224,255,${0.25 * glow})`,
            }}
          >
            Mission Memory
          </div>
          <div style={{ marginTop: 22, fontFamily: FONTS.body, fontSize: 32, color: COLORS.dim, textAlign: "center", maxWidth: 1300 }}>
            the tool stops doing tasks. the system starts compounding knowledge.
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={OUTRO} accent="cyan" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
